import React, { useMemo } from 'react'
import { Button, IconButton, Typography, makeStyles, Drawer, Paper } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import classnames from 'classnames'
import CloseIcon from '@material-ui/icons/Close'
import { useFlags } from 'shared/components/common/FlagsProvider'
import PresentationAcceptButton from '../PresentationAcceptButton'
import Quote, { QUOTE_CARD_MAX_WIDTHS } from '../../../Quote/Quote'
import QuoteTerms from '../../../Quote/QuoteTerms'
import CustomerViewQuoteAlerts from '../../../CustomerViewQuoteAlerts'
import { usePresentationNav } from '../PresentationNavContext'
import { getObjectLabels, formatCurrency, getQuoteNextPresentation } from '@paintscout/util/builder'
import { hasPicturesBlock, hasTermsBlock } from '../../util'

import { useQuote } from '../../../context/useQuote'
import { useClientOptions } from '@paintscout/ui-business'

const useStyles = makeStyles<Theme, PresentationQuoteDrawerProps>(
  (theme) => ({
    paper: {
      boxSizing: 'border-box',
      width: '90%',
      boxShadow: 'none',
      maxWidth: 1014,
      [theme.breakpoints.down('sm')]: {
        width: '95%'
      },
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      },
      ['@media print']: {
        display: 'none'
      }
    },
    modal: {
      ['@media print']: {
        display: 'none'
      }
    },
    drawerHeader: {
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      boxSizing: 'inherit',
      display: 'flex',
      height: 61,
      justifyContent: 'space-between',
      padding: theme.spacing(1, 4),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 1.5)
      }
    },
    drawerContent: {
      background: theme.palette.grey[100],
      flexGrow: 1,
      padding: theme.spacing(4),
      overflowY: 'auto',
      textAlign: 'left',
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1.5)
      }
    },
    alerts: {
      marginBottom: theme.spacing(3)
    },
    // Radius matches the document card above it: the legacy `Quote` paper is 2, the redesigned
    // (estimateNext) `QuoteNextView` document card is `borderRadius.lg` — so the terms box follows
    // whichever one renders, instead of reading as a square box under a rounded estimate (ENG-1479).
    termsWrapper: {
      borderRadius: 2,
      marginTop: theme.spacing(4),
      marginLeft: 'auto',
      marginRight: 'auto',
      maxWidth: QUOTE_CARD_MAX_WIDTHS.default,
      padding: '30px',
      [theme.breakpoints.down('md')]: {
        maxWidth: QUOTE_CARD_MAX_WIDTHS.md,
        padding: '20px'
      }
    },
    // Must stay declared after `termsWrapper` — same specificity, so source order in this sheet is
    // what makes it win.
    termsWrapperNext: {
      borderRadius: theme.borderRadius.lg
    },
    drawerFooter: {
      padding: theme.spacing(2, 4),
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      [theme.breakpoints.down('xs')]: {
        alignItems: 'flex-start',
        padding: theme.spacing(1.25, 1.5, 1)
      }
    },
    totalBlock: {
      marginLeft: theme.spacing(5),
      [theme.breakpoints.down('xs')]: {
        marginLeft: theme.spacing(1)
      }
    },
    totalLabel: {
      marginBottom: theme.spacing(1)
    },
    total: {
      lineHeight: 1,
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.1rem'
      }
    },
    acceptGridRoot: {
      justifyContent: 'flex-start',
      alignSelf: 'flex-end',
      padding: 0,
      flexGrow: 1
    },
    cartButtonMobile: {
      display: 'none',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    cartButtonTablet: {
      display: 'block',
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      }
    }
  }),
  { name: 'PresentationQuoteDrawer' }
)

export interface PresentationQuoteDrawerProps {
  classes?: StyleClasses<typeof useStyles>
}

function PresentationQuoteDrawer(props: PresentationQuoteDrawerProps) {
  const classes = useStyles(props)
  const { quote } = useQuote()
  const estimateNext = !!useFlags().estimateNext
  const { cartOpen, onOpenCart, onCloseCart, presentation: navPresentation } = usePresentationNav()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, quote, invoice: quote.is_invoice })
  const isImported = !!quote.importedContent?.primary

  // this helps react-pdf (imported quotes only) unmount cleaner when the drawer is closed
  const renderQuoteInDrawer = cartOpen || !isImported

  // the presentation already renders terms inline if it has a terms block — only show terms here
  // when it doesn't, so they aren't shown twice. Media works the same way, with the `pictures` block.
  //
  // The nav context carries the presentation that is actually on screen, so it has to win: the
  // saved template and the rendered document are not always the same one. A preview renders the
  // quote's snapshot or the builder's live Formik values, and the `current: true` lookup returns
  // the company's saved template — when only the rendered one holds the terms block, this showed
  // the terms twice (#22699). The lookup stays as the fallback for surfaces that mount the drawer
  // without a nav presentation.
  const presentation = useMemo(
    () => navPresentation ?? getQuoteNextPresentation({ options, quote, current: true }),
    [navPresentation, options, quote]
  )
  const showTerms = !hasTermsBlock(presentation)
  const showMediaSection = !hasPicturesBlock(presentation)

  return (
    <>
      <Button className={classes.cartButtonTablet} onClick={onOpenCart} variant={'dialog'}>
        {`View ${objectLabels.quote.value}`}
      </Button>
      <Button className={classes.cartButtonMobile} onClick={onOpenCart} variant={'dialog'} size={'small'}>
        {objectLabels.quote.value}
      </Button>
      <Drawer
        classes={{ paper: classes.paper, modal: classes.modal }}
        open={cartOpen}
        onClose={onCloseCart}
        anchor={'right'}
        disablePortal
      >
        <div className={classes.drawerHeader}>
          <Typography variant={'h2'}>{objectLabels.quote.value}</Typography>
          <IconButton onClick={onCloseCart} edge={'end'}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.drawerContent}>
          <div className={classes.alerts}>
            <CustomerViewQuoteAlerts revision={false} />
          </div>
          {renderQuoteInDrawer && <Quote inPresentation showMediaSection={showMediaSection} />}
          {showTerms && (
            <Paper
              classes={{
                root: classnames(classes.termsWrapper, { [classes.termsWrapperNext]: estimateNext })
              }}
            >
              <Typography variant={'h3'}>Terms</Typography>
              <QuoteTerms isQuoteView />
            </Paper>
          )}
        </div>
        <div className={classes.drawerFooter}>
          <PresentationAcceptButton classes={{ acceptGridRoot: classes.acceptGridRoot }} />
          <div className={classes.totalBlock}>
            <Typography variant={'h3'} className={classes.totalLabel}>
              Total
            </Typography>
            <Typography className={classes.total} variant={'h2'}>{`${formatCurrency({
              options,
              value: quote.totals.after_tax
            })}`}</Typography>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default PresentationQuoteDrawer
